import { CheckOutlined, CloseOutlined, MoreOutlined, RollbackOutlined } from '@ant-design/icons';
import {
  DeleteButtonProps,
  EditButtonProps,
  ListButtonProps,
  RefreshButton,
  RefreshButtonProps,
} from '@refinedev/antd';
import { Button, ButtonProps, Flex, Modal, Tooltip } from 'antd';
import useNotification from 'antd/es/notification/useNotification';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import PopupMenu from '@/components/general/PopupMenu';
import ProtectedDeleteButton from '@/components/protected/ProtectedDeleteButton';
import ProtectedEditButton from '@/components/protected/ProtectedEditButton';
import { usePermissionContext } from '@/contexts/PermissionContext';
import { useUiContext } from '@/contexts/UiContext';
import { useUserContext } from '@/contexts/UserContext';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { SERVICE_ORDER_STATUS, ServiceOrder } from '@/model/ServiceOrder/ServiceOrder';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

interface ServiceOrderShowHeaderButtonsProps {
  serviceOrder: ServiceOrder;
  refineButtonsProps: {
    editButtonProps: EditButtonProps | undefined;
    deleteButtonProps: DeleteButtonProps | undefined;
    refreshButtonProps: RefreshButtonProps | undefined;
    listButtonProps: ListButtonProps | undefined;
  };
  refetch: () => void;
  modifingAdminData?: boolean;
}

const ServiceOrderShowHeaderButtons = (props: ServiceOrderShowHeaderButtonsProps) => {
  const { t } = useTranslation();
  const { serviceOrderRepository } = useRepositoriesContext();
  const { isMediumScreen } = useUiContext();
  const { userWorkArea } = useUserContext();
  const { hasPermission } = usePermissionContext();
  const { serviceOrder, refineButtonsProps, refetch, modifingAdminData } = props;
  const { status, id } = serviceOrder;
  const { refreshButtonProps, editButtonProps, deleteButtonProps } = refineButtonsProps;
  const [modal, contextHolder] = Modal.useModal();
  const [notificationApi, notificationContextHolder] = useNotification();
  const disabledTooltipText = useMemo(
    () =>
      modifingAdminData
        ? t('Guarda los cambios de la sección de administración antes de cambiar el estado')
        : '',
    [modifingAdminData, t],
  );
  const changeStateButtonProps: ButtonProps[] = useMemo(() => {
    if (
      userWorkArea == USER_WORK_AREA.CUSTOMER_SERVICE &&
      status == SERVICE_ORDER_STATUS.REALIZADA
    ) {
      return [
        {
          key: 'sendToBilling',
          type: isMediumScreen ? 'text' : 'primary',
          children: t('Enviar a facturación'),
          icon: <CheckOutlined />,
          onClick: () => {
            modal.confirm({
              title: t('¿Estás seguro de que deseas enviar la orden a facturación?'),
              onOk: () => {
                void serviceOrderRepository
                  .updateServiceOrder({
                    id: id,
                    status: SERVICE_ORDER_STATUS.PENDIENTE_DE_FACTURACION,
                  })
                  .then(serviceOrder => {
                    const receptionsCreated = serviceOrder?.receptions_created ?? [];

                    if (receptionsCreated.length > 0) {
                      // Mostramos cada recepción creada para distinguirlas y abrir la correcta.
                      notificationApi.success({
                        message:
                          receptionsCreated.length > 1
                            ? t('Se crearon {{count}} recepciones asociadas al envío', {
                                count: receptionsCreated.length,
                              })
                            : t('Se ha creado la recepción asociada al envío'),
                        description: (
                          <Flex vertical gap={2}>
                            <span>
                              {receptionsCreated.length > 1
                                ? t('Seleccione cuál recepción desea abrir')
                                : t('Haga click para abrir la recepción en un nuevo enlace')}
                            </span>
                            {receptionsCreated.map((receptionId, index) => (
                              <Button
                                key={receptionId}
                                type="link"
                                style={{ padding: 0, height: 'auto' }}
                                onClick={() => {
                                  window.open(`/v2/receptions/show/${receptionId}`, '_blank');
                                }}>
                                {`${t('Recepción')} ${index + 1} - #${receptionId}`}
                              </Button>
                            ))}
                          </Flex>
                        ),
                      });
                    }

                    refetch();
                  });
              },
            });
          },
        },
      ];
    }

    if (userWorkArea != USER_WORK_AREA.ADMINISTRATION) {
      return [];
    }

    return status == SERVICE_ORDER_STATUS.PENDIENTE_DE_FACTURACION
      ? [
          {
            key: 'backToFinished',
            type: isMediumScreen ? 'text' : 'default',
            children: t('Volver a realizada'),
            icon: <RollbackOutlined />,
            onClick: () => {
              modal.confirm({
                title: t('¿Estás seguro de que deseas volver esta orden al estado de realizada?'),
                onOk: async () => {
                  await serviceOrderRepository.updateServiceOrder({
                    id: id,
                    status: SERVICE_ORDER_STATUS.REALIZADA,
                  });
                  refetch();
                },
              });
            },
          },
          {
            key: 'complete',
            type: isMediumScreen ? 'text' : 'primary',
            children: t('Finalizar'),
            icon: <CheckOutlined />,
            onClick: () => {
              modal.confirm({
                title: t('¿Estás seguro de que deseas finalizar la orden de servicio?'),
                content: t(
                  'Una vez finalizada, no se podrán realizar más modificaciones a esta orden de servicio',
                ),
                onOk: async () => {
                  await serviceOrderRepository.updateServiceOrder({
                    id: id,
                    status: SERVICE_ORDER_STATUS.FINALIZADA,
                  });
                  refetch();
                },
              });
            },
          },
        ]
      : [];
  }, [
    userWorkArea,
    status,
    isMediumScreen,
    t,
    modal,
    serviceOrderRepository,
    id,
    refetch,
    notificationApi,
  ]);
  // Bloqueo de botones si faltan datos de facturación
  const nonPaymentData: { tooltip: string; disabled: boolean } = useMemo(
    () =>
      serviceOrder.hours_to_pay == undefined || serviceOrder.kilometres_to_pay == undefined
        ? { tooltip: t('Faltan datos de facturación'), disabled: true }
        : { tooltip: '', disabled: false },
    [serviceOrder.hours_to_pay, serviceOrder.kilometres_to_pay, t],
  );
  const changeStateButtons = useMemo(
    () =>
      changeStateButtonProps.map((buttonProps: any) => {
        // Evita pasar "key" por spread, React lo exige explícito en JSX.
        const { key, ...restProps } = buttonProps;

        return (
          <Tooltip
            key={key}
            title={
              key == 'backToFinished'
                ? ''
                : disabledTooltipText
                  ? disabledTooltipText
                  : nonPaymentData.tooltip
            }>
            <Button
              {...restProps}
              disabled={
                key == 'backToFinished' || key == 'sendToBilling'
                  ? modifingAdminData
                  : modifingAdminData || nonPaymentData.disabled
              }
            />
          </Tooltip>
        );
      }),
    [changeStateButtonProps, disabledTooltipText, modifingAdminData, nonPaymentData],
  );
  const cancelButton = useMemo(
    () =>
      hasPermission('edit') &&
      (status == SERVICE_ORDER_STATUS.PENDIENTE_DE_COORDINACION ||
        status == SERVICE_ORDER_STATUS.COORDINADA) ? (
        <Tooltip title={disabledTooltipText}>
          <Button
            key={'cancel'}
            disabled={modifingAdminData}
            danger
            icon={<CloseOutlined />}
            type={isMediumScreen ? 'text' : 'default'}
            onClick={() => {
              modal.confirm({
                title: t('¿Estás seguro de que deseas cancelar la orden de servicio?'),
                onOk: async () => {
                  await serviceOrderRepository.updateServiceOrder({
                    id: id,
                    status: SERVICE_ORDER_STATUS.CANCELADO,
                  });
                  refetch();
                },
              });
            }}>
            {t('Cancelar')}
          </Button>
        </Tooltip>
      ) : null,
    [
      disabledTooltipText,
      hasPermission,
      id,
      isMediumScreen,
      modal,
      modifingAdminData,
      refetch,
      serviceOrderRepository,
      status,
      t,
    ],
  );
  const popupMenuContent: ReactNode[] = useMemo(() => {
    if (!hasPermission('edit') && !hasPermission('delete')) {
      return [];
    }

    const content: ReactNode[] = [];

    if (isMediumScreen && changeStateButtons.length > 0) {
      content.push(...changeStateButtons);
    }

    if (
      status == SERVICE_ORDER_STATUS.PENDIENTE_DE_COORDINACION ||
      status == SERVICE_ORDER_STATUS.COORDINADA ||
      status == SERVICE_ORDER_STATUS.REALIZADA
    ) {
      content.push(<ProtectedEditButton key="edit" {...editButtonProps} type="text" />);
    }

    if (isMediumScreen && cancelButton) {
      content.push(cancelButton);
    }

    if (
      status == SERVICE_ORDER_STATUS.PENDIENTE_DE_COORDINACION ||
      status == SERVICE_ORDER_STATUS.COORDINADA
    ) {
      content.push(<ProtectedDeleteButton key="delete" {...deleteButtonProps} type="text" />);
    }

    return content;
  }, [
    cancelButton,
    changeStateButtons,
    deleteButtonProps,
    editButtonProps,
    hasPermission,
    isMediumScreen,
    status,
  ]);

  return (
    <Flex gap={8} className="ServiceOrderShowHeaderButtons">
      <RefreshButton {...refreshButtonProps} hideText size={isMediumScreen ? 'small' : 'middle'} />
      {!isMediumScreen && cancelButton}
      {!isMediumScreen && changeStateButtons}
      {popupMenuContent.length > 0 && (
        <PopupMenu
          openButton={
            <Tooltip title={disabledTooltipText}>
              <Button
                disabled={modifingAdminData}
                icon={<MoreOutlined />}
                size={isMediumScreen ? 'small' : 'middle'}
              />
            </Tooltip>
          }
          menuContent={
            <Flex vertical align="start" gap={8}>
              {popupMenuContent}
            </Flex>
          }
        />
      )}
      {contextHolder}
      {notificationContextHolder}
    </Flex>
  );
};

export default ServiceOrderShowHeaderButtons;
